import {
  makeStyles,
  Badge,
  Button,
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from "@fluentui/react-components";
import { Link16Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { Tool } from "../../models/Tool";
import { FlatVsaaTag } from "../../models/VsaaTag";
import { SkanskaColorTokens } from "../../style/colors";
import { useMemo } from "react";
import { getDocumentIcon } from "@skanska/vsa-file-icon";
import ToolDownloadButton from "./ToolDownloadButton";

const useStyles = makeStyles({
  accordion: {
    borderBottom: "1px solid #ddd",
  },
  accordionTitle: {
    fontWeight: "bold",
  },
  toolItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "5px 10px",
    border: "1px solid #ddd",
    borderRadius: "5px",
    marginBottom: "5px",
  },
  toolInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  icon: {
    width: "16px",
    height: "16px",
  },
  removeButton: {
    marginLeft: "10px",
    backgroundColor: SkanskaColorTokens.color_tertiary_red_30,
    ":hover, :focus, :active": {
      backgroundColor: SkanskaColorTokens.color_tertiary_red_15,
      color: SkanskaColorTokens.color_secondary_white,
    },
  },
});

interface GroupedToolListProps {
  selectedTools: Tool[];
  onRemove: (toolId: string) => void;
  readonly: boolean;
}

const GroupedToolList = (props: GroupedToolListProps) => {
  const { selectedTools, onRemove, readonly } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const sortedSelectedTools = useMemo<Tool[]>(() => {
    const newSortedTools = selectedTools.sort((a, b) => {
      const aType = a.fieldValues?.tags["toolTypes"] as
        FlatVsaaTag[] | undefined;
      const bType = b.fieldValues?.tags["toolTypes"] as
        FlatVsaaTag[] | undefined;

      const aposition =
        aType !== undefined && aType.length !== 0 && aType[0].position
          ? aType[0].position
          : 0;

      const bposition =
        bType !== undefined && bType.length !== 0 && bType[0].position
          ? bType[0].position
          : 0;

      return aposition <= bposition ? -1 : 1;
    });

    return newSortedTools;
  }, [selectedTools]);

  const groupedTools = sortedSelectedTools?.reduce<Record<string, Tool[]>>(
    (groups, tool) => {
      const toolType = tool.fieldValues?.tags["toolTypes"] as FlatVsaaTag[];
      const typeTitle = toolType?.[0].title;

      if (!groups[typeTitle]) {
        groups[typeTitle] = [];
      }
      groups[typeTitle].push(tool);
      return groups;
    },
    {}
  );

  return (
    <>
      {Object.entries(groupedTools).map(([groupTitle, items]) => (
        <Accordion
          key={groupTitle}
          className={classes.accordion}
          collapsible
          defaultOpenItems={groupTitle}
        >
          <AccordionItem value={groupTitle}>
            <AccordionHeader>
              <div className={classes.accordionTitle}>
                {groupTitle} ({items.length})
              </div>
            </AccordionHeader>
            <AccordionPanel>
              {items.map((tool) => (
                <div key={tool.toolId} className={classes.toolItem}>
                  <span className={classes.toolInfo}>
                    {tool.fileUniqueId ? (
                      <img
                        src={getDocumentIcon(tool.fileType)}
                        className={classes.icon}
                      />
                    ) : (
                      <Link16Regular />
                    )}
                    {tool.title}
                    {tool.fieldValues?.booleans.archived ? (
                      <Badge appearance="filled" color="danger">
                        {t("archived")}
                      </Badge>
                    ) : !tool.fieldValues?.booleans.active ? (
                      <Badge appearance="outline" color="warning">
                        {t("inactive")}
                      </Badge>
                    ) : null}
                    {readonly && tool.fileUniqueId && (
                      <ToolDownloadButton tool={tool} />
                    )}
                  </span>
                  {!readonly && (
                    <Button
                      className={classes.removeButton}
                      onClick={() => tool.toolId && onRemove(tool.toolId)}
                    >
                      {t("delete")}
                    </Button>
                  )}
                </div>
              ))}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      ))}
    </>
  );
};

export default GroupedToolList;
